// Fix WH57 (2026-09-23): metas de venta por sucursal y mes. "Venta vs meta" de
// los KPIs de Gabriela es venta real / meta, y la meta de cada sucursal en cada
// mes no existia en ningun lado. La carga la evaluadora; al guardar, el backend
// recalcula los KPIs automaticos del mes.
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";

function fmtMXN(v) {
  if (v === null || v === undefined) return "—";
  return `$${Number(v).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;
}

export default function MetasVentaModal({ period, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/ops/kpis/metas-venta?period=${period}`)
      .then((res) => {
        if (!alive) return;
        setData(res.data);
        const d = {};
        (res.data.sucursales || []).forEach((s) => { d[s.branch_id] = s.meta ?? ""; });
        setDraft(d);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "No se pudieron cargar las metas de venta"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period]);

  const save = async () => {
    const metas = Object.entries(draft)
      .filter(([, v]) => v !== "" && v !== null && v !== undefined)
      .map(([branch_id, v]) => ({ branch_id, amount: Number(v) }));
    if (metas.some((m) => !(m.amount > 0))) {
      toast.error("Cada meta tiene que ser un monto mayor a 0");
      return;
    }
    if (metas.length === 0) {
      toast.error("Carga al menos una meta");
      return;
    }
    setSaving(true);
    try {
      const res = await api.put("/ops/kpis/metas-venta", { period, metas });
      toast.success(`Metas guardadas. ${res.data.recalculados} KPIs automáticos recalculados.`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron guardar las metas");
    } finally {
      setSaving(false);
    }
  };

  const puedeEditar = !!data?.puede_editar;
  const total = Object.values(draft).reduce((acc, v) => acc + (Number(v) > 0 ? Number(v) : 0), 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !saving && onClose()} data-testid="metas-venta-modal">
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-slate-900">Metas de venta · {period}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              "Venta vs meta" se calcula como venta del mes ÷ esta meta. La meta de la cadena es la suma de las sucursales.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="metas-venta-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading || !data ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase text-slate-400 border-b border-slate-100">
                  <th className="py-2 text-left">Sucursal</th>
                  <th className="py-2 text-right">Venta a la fecha</th>
                  <th className="py-2 text-right">Meta del mes (MXN)</th>
                </tr>
              </thead>
              <tbody>
                {(data.sucursales || []).map((s) => (
                  <tr key={s.branch_id} className="border-b border-slate-100">
                    <td className="py-2 font-medium text-slate-800">{s.name}</td>
                    <td className="py-2 text-right text-slate-500">{fmtMXN(s.venta)}</td>
                    <td className="py-2 text-right">
                      {puedeEditar ? (
                        <input
                          type="number"
                          min="0"
                          step="1000"
                          value={draft[s.branch_id] ?? ""}
                          onChange={(e) => setDraft((d) => ({ ...d, [s.branch_id]: e.target.value }))}
                          className="w-36 border border-slate-200 rounded-lg px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                          data-testid={`metas-venta-input-${s.branch_id}`}
                        />
                      ) : (
                        <span className="text-slate-700">{fmtMXN(s.meta)}</span>
                      )}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="py-2 font-bold text-slate-800">Cadena</td>
                  <td className="py-2 text-right font-semibold text-slate-600">{fmtMXN(data.venta_total)}</td>
                  <td className="py-2 text-right font-bold text-slate-800">{total > 0 ? fmtMXN(total) : "—"}</td>
                </tr>
              </tbody>
            </table>
            {!puedeEditar && (
              <p className="text-xs text-slate-500 italic">Las metas las carga únicamente la evaluadora de KPIs.</p>
            )}
            <div className="flex gap-3 pt-1">
              <button onClick={onClose} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50">
                {puedeEditar ? "Cancelar" : "Cerrar"}
              </button>
              {puedeEditar && (
                <button
                  onClick={save}
                  disabled={saving}
                  className="flex-1 py-2 bg-[#FFDF00] text-slate-900 rounded-lg text-sm font-semibold hover:bg-yellow-400 disabled:opacity-50"
                  data-testid="metas-venta-save"
                >
                  {saving ? "Guardando..." : "Guardar metas"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
